import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, AlertTriangle, Check, ChevronRight, ChevronDown, ChevronUp, Wrench, Search, ArrowRight, AlertCircle, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const troubleshootingData = [
  {
    category: "Post-Tuning Issues",
    icon: Wrench,
    issues: [
      {
        problem: "Check Engine Light After Tuning",
        severity: "medium",
        causes: [
          "We missed a DTC in the file - it happens occasionally on ECUs with unusual code sets",
          "A sensor is reading outside the range the ECU expects with the new calibration",
          "The tune changed a parameter that a related monitoring system doesn't like"
        ],
        solutions: [
          "Grab your OBD scanner and pull the codes. Write down the exact numbers (P0420, P2002, etc.)",
          "Send us those codes with your order number - we'll revise the file to suppress them at no charge",
          "Don't panic. A CEL after tuning almost always means a missed DTC, not a mechanical problem",
          "Some codes clear themselves after 50-100 miles of driving as the ECU adapts"
        ],
        prevention: "Tell us everything you want done upfront. If you want DPF, EGR, and AdBlue all deleted, say so in one go. We'll catch all the related codes in a single revision."
      },
      {
        problem: "Vehicle Won't Start After Flashing",
        severity: "high",
        causes: [
          "The flash was interrupted - power cut, cable bumped, laptop died mid-write",
          "Wrong file loaded. The ECU hardware version didn't match the file we modified",
          "File got corrupted during download or transfer to the flashing tool"
        ],
        solutions: [
          "Don't keep cranking the starter - that won't help and you'll drain the battery",
          "Check your battery voltage. If it's dropped below 12V, charge it first",
          "Try flashing your original backup file back. If you kept your backup (you did, right?), this should recover it",
          "If the original won't go in either, the ECU likely needs bench or boot mode recovery. Contact us with your order details",
          "This is fixable in almost every case. We've recovered ECUs that other shops gave up on"
        ],
        prevention: "Use a proper battery charger during every flash. Verify the file details match your ECU before writing. And for the love of all things mechanical, don't touch anything during the process."
      },
      {
        problem: "Limp Mode / Reduced Power After Tuning",
        severity: "high",
        causes: [
          "The ECU detected something it doesn't like and went into protection mode",
          "Turbo overboost protection kicked in - boost is hitting higher than the safety threshold allows",
          "Exhaust gas temps spiked beyond the ECU's comfort zone",
          "A sensor (MAF, MAP, or rail pressure) is reading outside the acceptable range for the new calibration"
        ],
        solutions: [
          "Scan for codes first. Limp mode always has a trigger code - find it and you've found the problem",
          "Clear the codes, take it for a careful drive, and see if it comes back. Sometimes it's a one-off",
          "If it comes back, the tune needs adjusting for your specific vehicle. Send us the codes and we'll revise",
          "Check the boost system for leaks while you're at it. A split hose will trigger overboost protection",
          "Make sure all the MAF and MAP sensor plugs are properly seated"
        ],
        prevention: "Make sure your vehicle is mechanically sound before flashing any tune. A dodgy boost hose or failing sensor will cause problems that look like bad tuning."
      },
      {
        problem: "Worse Fuel Economy After Performance Tune",
        severity: "low",
        causes: [
          "You're enjoying the extra power. More right foot = more fuel. It's physics, not a bad tune",
          "The tune is calibrated for performance, which means more fuel at higher loads",
          "You're still in the adaptation phase and driving differently because the car feels different"
        ],
        solutions: [
          "A performance tune will use more fuel if you use the power. That's expected and normal",
          "Drive one full tank conservatively and compare. The economy at cruise should be similar to stock or slightly better",
          "If fuel saving is your main goal, ask us for an economy-focused tune instead",
          "Scan for any fault codes just to rule out an issue. Unlikely, but worth checking"
        ],
        prevention: "Be honest about what you want when ordering. If economy matters more than power, we'll calibrate for that. We can also do balanced tunes that improve both."
      },
      {
        problem: "Black Smoke (Diesel Engines)",
        severity: "medium",
        causes: [
          "Smoke limiter maps were set too aggressively in the tune",
          "More fuel is going in than the available air can burn cleanly",
          "EGR delete without the fueling maps being adjusted to compensate"
        ],
        solutions: [
          "A small puff of smoke under hard acceleration is normal on a performance diesel tune - that's just the nature of running richer",
          "Constant visible smoke at all throttle positions means the tune needs work. Get back to us",
          "We'll revise the smoke limiter and fueling maps to clean it up while keeping the power gains",
          "Also check your air filter. A restricted filter starves the engine of air and makes smoke worse"
        ],
        prevention: "Let us know if you want clean power. We can tune for strong gains with minimal smoke - it's a trade-off we adjust to your preference."
      },
      {
        problem: "Rough Idle or Hunting RPM",
        severity: "medium",
        causes: [
          "Idle fuel maps got modified when they shouldn't have been",
          "There's an air leak in the intake system that wasn't there before (or was there but masked by the old calibration)",
          "An existing mechanical issue that the new tune made more noticeable"
        ],
        solutions: [
          "Check for vacuum or boost leaks first. Spray some brake cleaner around intake connections while it's idling - if the RPM changes, you've found your leak",
          "Pull any stored fault codes related to idle control or air metering",
          "Give it 50-100 miles of normal driving. ECUs have adaptive idle algorithms that take time to settle",
          "If it's still rough after that, the idle maps may need revision. Send us the details"
        ],
        prevention: "Fix any existing intake leaks before flashing. A tune can't compensate for mechanical problems, and it shouldn't have to."
      }
    ]
  },
  {
    category: "DPF Delete Issues",
    icon: AlertTriangle,
    issues: [
      {
        problem: "DPF Light Still On After Delete",
        severity: "medium",
        causes: [
          "There's a DPF-related code we didn't catch. Some ECUs have 20+ codes tied to the DPF system",
          "The physical DPF is still in place and partially blocked, so the differential pressure sensor is still reading restriction",
          "The differential pressure sensor itself is still sending data the ECU doesn't know what to do with"
        ],
        solutions: [
          "Pull the specific codes and send them to us. We'll revise the file to catch whatever we missed",
          "If you're keeping the physical DPF in place, let us know. We need to handle the pressure sensor monitoring differently",
          "For a clean delete, the DPF should be gutted or replaced with a straight pipe. Software alone won't fix the backpressure problem"
        ],
        prevention: "Tell us upfront whether the physical filter is being removed or staying in place. It changes which maps and sensors we need to disable."
      },
      {
        problem: "Regen Still Trying to Run After Delete",
        severity: "medium",
        causes: [
          "Not all regeneration triggers were disabled in the file. Some ECUs have passive, active, AND forced regen strategies",
          "On certain vehicles, the transmission ECU can also trigger regen events",
          "There might be secondary regen conditions we didn't disable"
        ],
        solutions: [
          "If you notice extra fuel consumption at highway speeds or a fuel smell from the exhaust, regen is still active",
          "Tell us exactly when it seems to happen (speed, RPM, driving conditions) so we can identify the trigger",
          "We'll check all regen strategies in the file and disable anything we missed",
          "Some vehicles need both ECU and TCU modification for a complete delete. We'll advise if yours is one of them"
        ],
        prevention: "A proper DPF delete disables every regeneration pathway. If something slipped through, the revision is free."
      },
      {
        problem: "Performance Feels Off After DPF Delete",
        severity: "low",
        causes: [
          "Removing the DPF significantly changes exhaust backpressure. The turbo may behave differently without that restriction",
          "The ECU may still be running some parameters based on the assumption that a DPF is present",
          "If the exhaust piping wasn't done well, there could be leaks at the new joints"
        ],
        solutions: [
          "The engine needs time to adapt. Give it a few hundred miles before judging",
          "A combined performance tune with DPF delete optimises the calibration for the new exhaust flow",
          "Check all exhaust joints for leaks. A boost leak downstream of the turbo will kill performance",
          "If the turbo seems laggy, have the actuator checked. Sometimes the change in backpressure exposes a weak actuator"
        ],
        prevention: "Combining DPF delete with a performance tune gives the best result. The tune accounts for the changed exhaust characteristics."
      }
    ]
  },
  {
    category: "EGR Delete Issues",
    icon: AlertCircle,
    issues: [
      {
        problem: "EGR Fault Codes After Delete",
        severity: "medium",
        causes: [
          "Some ECUs monitor EGR position, flow rate, AND temperature independently. If we only disabled one or two, the third will throw codes",
          "The EGR valve was physically disconnected but not blanked, or the software wasn't updated",
          "Manufacturer-specific EGR codes that weren't in the standard DTC set"
        ],
        solutions: [
          "Pull the specific fault codes and send them over. The exact code tells us which monitoring routine is still active",
          "If you've only blanked the EGR physically but haven't done the software side, you need our ECU modification",
          "Keep the EGR valve plugged in electrically if you're just using a blanking plate - some ECUs monitor the electrical circuit",
          "We'll revise the file to catch any monitors we missed. No charge for revisions"
        ],
        prevention: "A complete EGR delete means disabling the valve control, flow monitoring, position monitoring, and temperature monitoring. If your tuner only did one of those, codes are inevitable."
      },
      {
        problem: "Intake Still Getting Carbon After EGR Delete",
        severity: "low",
        causes: [
          "The PCV (crankcase breather) system is still routing oil vapour into the intake. That's not the EGR's fault",
          "The turbo's compressor seals might be letting a small amount of oil past",
          "If you used a blanking plate instead of full removal, check it's sealing properly"
        ],
        solutions: [
          "EGR delete eliminates exhaust gas contamination, but oil vapour from the PCV system is a separate issue entirely",
          "Fit an oil catch can to the PCV system if you want a truly clean intake",
          "Have the turbo checked for excessive oil consumption. A worn compressor seal will push oil into the intake regardless of EGR status",
          "Make sure the EGR blank is properly sealed with no gaps"
        ],
        prevention: "If you're going through the trouble of EGR delete for intake cleanliness, an oil catch can on the PCV is worth the extra $50-100. It catches what the EGR delete can't."
      }
    ]
  },
  {
    category: "AdBlue Delete Issues",
    icon: AlertTriangle,
    issues: [
      {
        problem: "AdBlue Warning Still Showing",
        severity: "medium",
        causes: [
          "The AdBlue system has multiple monitoring pathways - SCR efficiency, NOx sensors, tank level, quality sensor, dosing rate. Miss one and you get a warning",
          "Some vehicles have redundant warning systems in the instrument cluster that operate independently from ECU DTCs",
          "The dashboard warning may be driven by a separate module that needs coding"
        ],
        solutions: [
          "Scan the ECU for all AdBlue/SCR/NOx related codes. There might be 10-15 different ones on a complex system",
          "On some Mercedes and BMW models, the instrument cluster needs a separate coding adjustment",
          "We'll disable all remaining monitors in the next revision. Tell us which specific warning you're seeing",
          "Physically disconnecting the AdBlue tank sensors can help prevent false readings on some vehicles"
        ],
        prevention: "A proper AdBlue delete covers SCR efficiency calculations, all NOx sensors (upstream and downstream), tank level monitoring, quality sensor, dosing rate, and the temperature sensor. If any of those were missed, warnings will appear."
      },
      {
        problem: "Speed Limiter Still Active After Delete",
        severity: "high",
        causes: [
          "The AdBlue-related speed limiter wasn't disabled in the ECU software",
          "Some vehicles have progressive speed limiters with multiple stages - the first might be removed but a second kicks in later",
          "The countdown timer is still running independently of the other deletions"
        ],
        solutions: [
          "Tell us exactly what speed you're being limited to and when it kicks in",
          "Some vehicles start at 120 km/h, then drop to 80, then to 50. We need to remove all stages",
          "The AdBlue countdown (the one that says 'engine will not restart in X km') needs specific attention",
          "Complete electrical disconnection of the AdBlue system may be needed alongside the software delete"
        ],
        prevention: "Speed limiter removal is absolutely critical in any AdBlue delete. If yours was missed, that's a priority revision - contact us immediately."
      },
      {
        problem: "NOx Sensor Faults After Delete",
        severity: "medium",
        causes: [
          "Not all NOx sensor DTCs were removed. Most vehicles have two NOx sensors - one upstream, one downstream - and they're monitored independently",
          "The ratio monitoring between the two sensors is still active",
          "SCR efficiency calculations are still running and comparing sensor readings"
        ],
        solutions: [
          "Send us all the NOx-related fault codes. P20EE and P203F are the common ones but there are many more",
          "We'll disable monitoring for both upstream and downstream NOx sensors",
          "The SCR efficiency calculation needs to go too - it's what compares the two sensor readings"
        ],
        prevention: "Modern AdBlue systems use 2-3 NOx sensors. A proper delete disables all of them plus the efficiency calculation that compares their readings."
      }
    ]
  },
  {
    category: "Flash Tool Problems",
    icon: HelpCircle,
    issues: [
      {
        problem: "Tool Won't Connect to the Vehicle",
        severity: "medium",
        causes: [
          "Wrong vehicle or protocol selected in the tool software. Easy mistake, especially with sub-models",
          "Dodgy OBD cable or a damaged port. The pins in OBD ports get bent more often than you'd think",
          "The vehicle's immobiliser is active (key not in the right position)",
          "Battery voltage is too low for reliable communication"
        ],
        solutions: [
          "Double-check you've selected the exact right vehicle and ECU type in your tool. A 2016 and 2017 might use different protocols",
          "Inspect the OBD port pins for bent or corroded contacts. Try a different cable if you have one",
          "Turn the ignition to ON (dash lights on) but don't start the engine",
          "Hook up a battery charger. Communication drops out below about 12.3V on some ECUs",
          "If it's a USB tool, try a different USB cable and port. Cheap cables are the cause of half the 'connection failed' errors we hear about"
        ],
        prevention: "Keep your tool firmware updated. Use quality cables. And always charge the battery before starting - it costs nothing and prevents the most common issue."
      },
      {
        problem: "Flash Interrupted Mid-Write",
        severity: "high",
        causes: [
          "Battery voltage dropped during the write process",
          "A cable connection came loose (OBD, USB, or power)",
          "The computer went to sleep or triggered a Windows update. Yes, we've seen this happen",
          "USB connection interrupted by a notification or device conflict"
        ],
        solutions: [
          "Don't turn anything off. Don't disconnect anything. Don't panic",
          "Wait for the tool to timeout on its own. It might take several minutes",
          "If the tool has a recovery mode, use it. Most professional tools do",
          "If the ECU is bricked, bench mode or boot mode recovery will usually bring it back",
          "Contact your tool manufacturer's support. They deal with this regularly"
        ],
        prevention: "Battery charger. Disable computer sleep mode. Disable screen lock. Close all other programs. Use a quality USB cable. Lock the door so nobody walks into the cable. We're not joking about that last one."
      },
      {
        problem: "Checksum Error When Trying to Write",
        severity: "medium",
        causes: [
          "The modified file has an incorrect checksum. This shouldn't happen with our files, but if it does, something went wrong",
          "The file was corrupted during download or when you transferred it to the tool's memory card",
          "You're accidentally trying to flash the wrong file to the ECU"
        ],
        solutions: [
          "Re-download the file from your order. If the download was interrupted or your connection was unstable, the file could be corrupt",
          "Compare the file size to your original - they should be identical. If they're not, something's wrong",
          "Contact us with your order number. We'll verify the file on our end and resend if needed",
          "Some tools auto-correct checksums during the write process - check if yours has that option"
        ],
        prevention: "We include checksum correction in every file we send. If you're getting a checksum error, re-download first. If it persists, it's not the file - it's the transfer."
      }
    ]
  }
];

const TroubleshootingGuidePage = () => {
  useCanonical('https://ecuflashservice.com/troubleshooting-guide');
  const [openItems, setOpenItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleItem = (categoryIndex, issueIndex) => {
    const key = `${categoryIndex}-${issueIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-[#FF003C] bg-[#FF003C]/10 border-[#FF003C]/30';
      case 'medium': return 'text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]/30';
      case 'low': return 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]/30';
      default: return 'text-[#888] bg-[#888]/10 border-[#888]/30';
    }
  };

  const filteredData = troubleshootingData.map(category => ({
    ...category,
    issues: category.issues.filter(
      issue => 
        issue.problem.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.causes.some(c => c.toLowerCase().includes(searchTerm.toLowerCase())) ||
        issue.solutions.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  })).filter(category => category.issues.length > 0);

  const totalIssues = troubleshootingData.reduce((acc, cat) => acc + cat.issues.length, 0);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "ECU Tuning Troubleshooting Guide - Common Issues & Solutions",
    "description": "Complete troubleshooting guide for ECU tuning issues. Solutions for check engine lights, limp mode, DPF/EGR/AdBlue delete problems, and flash tool errors.",
    "author": {
      "@type": "Organization",
      "name": "ECU Flash Service"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ECU Flash Service",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ecuflashservice.com/favicon-512.png"
      }
    },
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": "https://ecuflashservice.com/troubleshooting-guide",
    "image": "https://ecuflashservice.com/favicon-512.png"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ecuflashservice.com" },
      { "@type": "ListItem", "position": 2, "name": "Troubleshooting Guide", "item": "https://ecuflashservice.com/troubleshooting-guide" }
    ]
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ECU Tuning Problems & Solutions | Fix Check Engine Light After Remap</title>
        <meta name="description" content="Troubleshooting guide for ECU tuning issues. Fix check engine light after tune, limp mode solutions, DPF delete problems, EGR fault codes, AdBlue errors solved." />
        <meta name="keywords" content="ECU tuning problems, check engine light after tune, limp mode fix, DPF delete issues, EGR delete problems, AdBlue fault codes" />
        <meta property="og:title" content="ECU Tuning Troubleshooting Guide" />
        <meta property="og:description" content="Complete solutions for common ECU tuning and delete problems." />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://ecuflashservice.com/favicon-512.png" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</span>
            </Link>
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
              <span>Get Started</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <Wrench className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Support Guide</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Troubleshooting</span>{' '}
            <span className="text-[#00F0FF]">Guide</span>
          </h1>
          <p className="text-[#888] text-lg mb-8 max-w-2xl mx-auto">
            Something not right after your tune? We've seen it all. Find your issue below and we'll walk you through fixing it.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
            <input
              type="text"
              placeholder="Describe your issue (e.g., 'check engine light', 'won't start')"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#00F0FF]/50 focus:outline-none"
              data-testid="troubleshooting-search"
            />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-6 px-6 bg-[#0A0A0A] border-y border-white/10">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-wrap gap-3 justify-center">
            {troubleshootingData.map((category, index) => (
              <a
                key={index}
                href={`#category-${index}`}
                className="px-4 py-2 bg-[#1A1A1A] border border-white/10 text-sm text-[#888] hover:text-white hover:border-[#00F0FF]/30 transition"
              >
                {category.category}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Troubleshooting Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {filteredData.map((category, categoryIndex) => (
            <div key={categoryIndex} id={`category-${categoryIndex}`} className="mb-12 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6 pb-2 border-b border-white/10">
                <category.icon className="w-6 h-6 text-[#00F0FF]" />
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00F0FF]">
                  {category.category}
                </h2>
              </div>
              
              <div className="space-y-4">
                {category.issues.map((issue, issueIndex) => {
                  const key = `${categoryIndex}-${issueIndex}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={issueIndex} className="bg-[#0A0A0A] border border-white/10 hover:border-white/20 transition">
                      <button
                        onClick={() => toggleItem(categoryIndex, issueIndex)}
                        className="w-full flex items-center justify-between p-5 text-left"
                        data-testid={`issue-toggle-${categoryIndex}-${issueIndex}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 text-xs font-bold uppercase border ${getSeverityColor(issue.severity)}`}>
                            {issue.severity}
                          </span>
                          <span className="text-white font-semibold">{issue.problem}</span>
                        </div>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-[#00F0FF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#666] flex-shrink-0" />
                        )}
                      </button>
                      
                      {isOpen && (
                        <div className="px-5 pb-5 pt-0 space-y-4">
                          {/* Causes */}
                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-[#FF003C] mb-2">What's Probably Going On</h4>
                            <ul className="space-y-1">
                              {issue.causes.map((cause, i) => (
                                <li key={i} className="flex items-start gap-2 text-[#888] text-sm">
                                  <ChevronRight className="w-4 h-4 text-[#FF003C] flex-shrink-0 mt-0.5" />
                                  {cause}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Solutions */}
                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-[#00FF88] mb-2">How to Fix It</h4>
                            <ul className="space-y-1">
                              {issue.solutions.map((solution, i) => (
                                <li key={i} className="flex items-start gap-2 text-[#888] text-sm">
                                  <Check className="w-4 h-4 text-[#00FF88] flex-shrink-0 mt-0.5" />
                                  {solution}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Prevention */}
                          {issue.prevention && (
                            <div className="p-3 bg-[#00F0FF]/5 border border-[#00F0FF]/20">
                              <h4 className="text-xs font-bold uppercase tracking-wider text-[#00F0FF] mb-1">Avoid This Next Time</h4>
                              <p className="text-sm text-[#888]">{issue.prevention}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredData.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#888]">No issues found matching "{searchTerm}"</p>
              <p className="text-sm text-[#666] mt-2">Try different words, or browse the categories above</p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">Issue Not Listed Here?</h2>
            <p className="text-[#888]">We're here to help. Most problems have a straightforward fix once we know the details.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#080808] border border-white/10 p-6">
              <h3 className="font-heading text-lg font-bold uppercase text-[#00F0FF] mb-2">File Revision</h3>
              <p className="text-[#888] text-sm mb-4">
                If the problem is with the tune itself - wrong codes, missing DTC removal, calibration issue - we'll revise the file for free. Just reply to your order email with the fault codes and a description of what's happening.
              </p>
              <Link to="/portal" className="text-[#00F0FF] text-sm hover:underline inline-flex items-center gap-1">
                Go to Customer Portal <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="bg-[#080808] border border-white/10 p-6">
              <h3 className="font-heading text-lg font-bold uppercase text-[#00F0FF] mb-2">Technical Support</h3>
              <p className="text-[#888] text-sm mb-4">
                For flash tool issues, vehicle-specific questions, or anything else that doesn't fit neatly into the categories above - drop us a line. We've probably seen your exact problem before.
              </p>
              <Link to="/contact" className="text-[#00F0FF] text-sm hover:underline inline-flex items-center gap-1">
                Contact Support <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition inline-flex items-center justify-center gap-2">
              <span>Contact Support</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/faq" className="btn-skew border border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-[#00F0FF] transition">
              <span>View FAQ</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TroubleshootingGuidePage;
